import { request } from './api';

/** Client des écrans d'administration des accès. */

export interface Utilisateur {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'user';
  profil_id: string | null;
  role_id: string | null;
  manager_id: string | null;
  salarie_id: string | null;
  statut_compte: 'cree' | 'invite' | 'actif' | 'suspendu' | 'desactive';
  invite_le: string | null;
  derniere_connexion_at: string | null;
  profil_nom: string | null;
  role_nom: string | null;
  manager_nom: string | null;
  salarie_nom: string | null;
  peut_se_connecter: boolean;
}

export interface ActionModule {
  code: string;
  libelle: string;
  aide?: string;
}
export interface ModuleApplicatif {
  code: string;
  libelle: string;
  description?: string;
  actions: ActionModule[];
}

export interface Profil {
  id: string;
  nom: string;
  description: string | null;
  systeme: boolean;
  actif: boolean;
  permissions: Record<string, string[]>;
  nb_utilisateurs: number;
}

export interface RoleAcces {
  id: string;
  nom: string;
  description: string | null;
  portee: 'soi' | 'equipe' | 'entreprise';
  ordre: number;
  systeme: boolean;
  actif: boolean;
  nb_utilisateurs: number;
}

export interface FicheSalarieCourte {
  id: string;
  nom: string;
  matricule: string | null;
  /** Nom de l'utilisateur qui la porte déjà — une fiche ne se partage pas. */
  rattachee_a: string | null;
}

const base = '/api/admin/acces';

export const accesApi = {
  modules: () => request<{ modules: ModuleApplicatif[] }>(`${base}/modules`),

  utilisateurs: {
    liste: () => request<{ utilisateurs: Utilisateur[] }>(`${base}/utilisateurs`),
    creer: (body: Partial<Utilisateur>) =>
      request<{ utilisateur: Utilisateur }>(`${base}/utilisateurs`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    modifier: (id: string, body: Partial<Utilisateur>) =>
      request<{ utilisateur: Utilisateur }>(`${base}/utilisateurs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    changerStatut: (id: string, statut: Utilisateur['statut_compte']) =>
      request<{ utilisateur: Utilisateur }>(`${base}/utilisateurs/${id}/statut`, {
        method: 'POST',
        body: JSON.stringify({ statut }),
      }),
  },

  profils: {
    liste: () => request<{ profils: Profil[] }>(`${base}/profils`),
    creer: (body: { nom: string; description?: string | null; permissions?: Record<string, string[]> }) =>
      request<{ ok: boolean; id: string }>(`${base}/profils`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    modifier: (
      id: string,
      body: { nom?: string; description?: string | null; actif?: boolean; permissions?: Record<string, string[]> },
    ) =>
      request<{ ok: boolean }>(`${base}/profils/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },

  roles: {
    liste: () => request<{ roles: RoleAcces[] }>(`${base}/roles`),
    creer: (body: { nom: string; portee: RoleAcces['portee']; description?: string | null }) =>
      request<{ ok: boolean; id: string }>(`${base}/roles`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    modifier: (id: string, body: Partial<Omit<RoleAcces, 'id' | 'nb_utilisateurs' | 'systeme'>>) =>
      request<{ ok: boolean }>(`${base}/roles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },

  fichesSalaries: () => request<{ fiches: FicheSalarieCourte[] }>(`${base}/fiches-salaries`),
};
